import React, { useState } from "react";
import { getSupabase } from "./supabaseClient";
import {
  Input,
  Button,
  VStack,
  Box,
  HStack,
  List,
  ListItem,
  Link,
} from "@chakra-ui/react";
import Select from "react-select";
import {
  prompts,
  data_column,
  file_reference_column,
  bucketName,
  tableName,
  num_levels_JSON,
  bucketName_files,
  supabaseUrl,
} from "./env.js";
import jsonToFormattedText from "./JSONToMarkdown.js"; // Adjusted name for clarity
import Modal from "react-modal";
import ResultsTable from "./table/ResultsTable.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faFile,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import "../css/JSONUploader.css"; // Ensure this file contains relevant CSS for styling

const openMarkdownInNewTab = (fileData) => {
  const newWindow = window.open("", "_blank");
  newWindow.document.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Formatted JSON Viewer</title>
      </head>
      <body>
        <div id="content">${fileData}</div>
      </body>
    </html>
  `);
  newWindow.document.close();
};

const JsonUploader = () => {
  const [file, setFile] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedColumns, setSelectedColumns] = useState(
    prompts.map(([promptName]) => ({ value: promptName, label: promptName }))
  );
  const [formattedText, setFormattedText] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [previousFolderPath, setPreviousFolderPath] = useState("");

  const columnOptions = prompts.map(([promptName]) => ({
    value: promptName,
    label: promptName,
  }));

  const tocItems = formattedText
    .split("\n")
    .filter((line) => line.startsWith("#"))
    .map((line) => ({
      id: line.replace(/#/g, "").trim().replace(/\s+/g, "-").toLowerCase(),
      text: line.replace(/#/g, "").trim(),
    }));

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setFormattedText(""); // Clear previous preview
    setJsonText("");
    setRows([]); // Clear previous rows
    setSelectedRows([]);
  };

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
  };

  const handleColumnChange = (selectedOptions) => {
    setSelectedColumns(selectedOptions);
  };

  const handlePreview = async () => {
    if (!file) {
      alert("Please select a file to preview.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        console.log("Parsed JSON:", json);

        // Convert JSON to formatted text for preview
        const formattedText = jsonToFormattedText(json);
        console.log("Converted Formatted Text:", formattedText);
        setFormattedText(formattedText);
        setJsonText(JSON.stringify(json, null, 2)); // Store JSON string for upload
      } catch (error) {
        console.error("Error parsing JSON file:", error);
        alert("Error parsing JSON file");
      }
    };
    reader.readAsText(file);
  };

  const handleFetchRows = async () => {
    const supabase = await getSupabase();
    if (!query || selectedColumns.length === 0) {
      alert("Please provide a query and select at least one column.");
      return;
    }

    const { data: sessionData, error: sessionError } =
      await supabase.auth.getSession();

    if (sessionError) {
      console.error("Error getting session:", sessionError);
      return;
    }

    const userId = sessionData?.session?.user?.id;

    if (!userId) {
      console.error(
        "User ID not found in session. Cannot proceed with upload."
      );
      return;
    }
    console.log(query)
    console.log("User ID:", userId);

    const columnName = selectedColumns.map((option) => option.value).join(", ");

    const { data: fetchData, error: fetchError } = await supabase
      .from(tableName)
      .select("*")
      .ilike(columnName, `%${query}%`)
      // .eq("user_id", userId);

    if (fetchError) {
      console.error("Error fetching rows from Supabase:", fetchError);
      alert("Error fetching rows from Supabase");
    } else {
      console.log("Rows fetched successfully:", fetchData);
      setRows(fetchData);
      setSelectedRows(new Array(fetchData.length).fill(false)); // Initialize all rows as not selected
    }
  };

  const handleRowSelection = (index) => {
    const updatedRows = [...selectedRows];
    updatedRows[index] = !updatedRows[index]; // Toggle the selection state
    setSelectedRows(updatedRows);
  };

  const handleUpload = async () => {
    const supabase = await getSupabase();
    if (!jsonText || !query || selectedColumns.length === 0) {
      alert("Please provide a file, query, and select at least one column.");
      return;
    }

    console.log("selectedrows: ", selectedRows)
    console.log("rows: ", rows)

    const selectedRowIds = rows
      .filter((row, index) => selectedRows[index])
      .map((row) => row.id);
    
    console.log("selectedrowid: ", selectedRowIds)

    if (selectedRowIds.length === 0) {
      alert("Please select at least one row to update.");
      return;
    }

    // Upload the JSON file to Supabase storage
    const blob = new Blob([jsonText], { type: "application/json" });
    const fileName = `notes_${new Date().toISOString()}.json`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(fileName, blob);

    if (uploadError) {
      console.error("Error uploading file to Supabase:", uploadError);
      alert("Error uploading file to Supabase");
      return;
    } else {
      console.log("File uploaded successfully:", uploadData);
    }

    const updateObject = {
      [data_column]: jsonText, // Use the dynamic column name from data_column
    };

    // Update selected rows in the Supabase table
    const { data: updateData, error: updateError } = await supabase
      .from(tableName)
      .update(updateObject)
      .in("id", selectedRowIds);

    if (updateError) {
      console.error("Error updating rows in Supabase:", updateError);
      alert("Error updating rows in Supabase");
    } else {
      console.log("Rows updated successfully:", updateData);
      alert("File uploaded and rows updated successfully!");
    }
  };

  const handleViewJSONFiles = (jsonContent) => {
    try {
      const jsonData = JSON.parse(jsonContent);
      const formattedText = jsonToFormattedText(jsonData);
      openMarkdownInNewTab(formattedText);
    } catch (error) {
      console.error("Error parsing JSON content:", error);
      alert("Error parsing JSON content");
    }
  };

  const handleViewReports = async (folderPath) => {
    const supabase = await getSupabase();
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setCurrentFolderPath(folderPath);
      setModalIsOpen(true);
    }
  };

  const handleGoUpOneLevel = async () => {
    const parentFolderPath = currentFolderPath
      .split("/")
      .slice(0, -1)
      .join("/");
    handleViewReports(parentFolderPath);
  };

  const handleGoDownOneLevel = async () => {
    handleViewFolderFiles(previousFolderPath);
  };

  const handleViewFolderFiles = async (folderPath) => {
    const supabase = await getSupabase();
    setPreviousFolderPath(currentFolderPath);
    setCurrentFolderPath(folderPath);

    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
    }
  };

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderJson = (json, parentId = "", level = 0) => {
    if (level > num_levels_JSON) {
      return (
        <Box ml={4}>
          <pre>{JSON.stringify(json, null, 2)}</pre>
        </Box>
      );
    }
    return Object.entries(json).map(([key, value]) => {
      const id = parentId ? `${parentId}-${key}` : key;
      const isExpanded = expandedSections[id];

      if (typeof value === "object" && value !== null) {
        return (
          <Box key={id} mt={2} id={id}>
            <div
              onClick={() => toggleSection(id)}
              style={{ cursor: "pointer", fontWeight: "bold" }}
            >
              {isExpanded ? "▼" : "►"} {key}
            </div>
            {isExpanded && <Box ml={4}>{renderJson(value, id, level + 1)}</Box>}
          </Box>
        );
      } else {
        return (
          <Box key={id} ml={4} id={id}>
            <strong>{key}:</strong> {value}
          </Box>
        );
      }
    });
  };

  return (
    <VStack spacing={4} align="stretch">
      <Input type="file" accept=".json" onChange={handleFileChange} />
      <Input placeholder="Query" value={query} onChange={handleQueryChange} />
      <Select
        isMulti
        options={columnOptions}
        value={selectedColumns}
        onChange={handleColumnChange}
        placeholder="Select columns to update"
      />
      <div>
        <Button className="button" onClick={handlePreview}>
          Preview
        </Button>
        <HStack spacing={4} align="stretch">
          <Box width="70%" overflowY="auto" maxHeight="500px" id="preview">
            <div>{file && jsonText && renderJson(JSON.parse(jsonText))}</div>
          </Box>
          <Box
            width="30%"
            overflowY="auto"
            maxHeight="500px"
            padding="10px"
            borderLeft="1px solid #ccc"
          >
            <List spacing={2}>
              {tocItems.map((item) => (
                <ListItem key={item.id}>
                  <Link
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .querySelector(`#preview #${item.id}`)
                        .scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {item.text}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Box>
        </HStack>
      </div>
      <Button className="button" onClick={handleFetchRows}>
        Fetch Rows
      </Button>
      {rows.length > 0 && (
        <ResultsTable
          results={rows}
          setResults={setRows}
          prompts={prompts}
          selectedRows={selectedRows}
          handleCheckboxChange={handleRowSelection}
          handleViewJSONFiles={handleViewJSONFiles}
          handleViewFolderFiles={handleViewReports}
          currentFolderPath={currentFolderPath}
          previousFolderPath={previousFolderPath}
          handleGoUpOneLevel={handleGoUpOneLevel}
          handleGoDownOneLevel={handleGoDownOneLevel}
          supabaseUrl={supabaseUrl}
          bucketName_files={bucketName_files}
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          currentFolderFiles={currentFolderFiles}
          showCheckboxes={true} // Ensure checkboxes are shown
        />
      )}
      <Button
        className="button"
        onClick={handleUpload}
        disabled={!formattedText}
      >
        Upload and Store
      </Button>
    </VStack>
  );
};

export default JsonUploader;
